import { useEffect, useRef } from 'react';
import { useAuth } from 'react-oidc-context';

import { AUTH_BROADCAST_MESSAGE, getAuthBroadcastChannel } from '@/utils/helpers/broadcastAuth';
import { getOidcUser } from '@/utils/helpers/userOidcHelper';

export const useCrossTabAuthSync = (onLogout: (isOpener: boolean) => void | Promise<void>) => {
  const auth = useAuth();
  const isLoggingOutRef = useRef(false);
  const savedOnLogout = useRef(onLogout);

  useEffect(() => {
    savedOnLogout.current = onLogout;
  }, [onLogout]);

  useEffect(() => {
    const handleLogout = (isOpener: boolean) => {
      if (isLoggingOutRef.current) return;
      isLoggingOutRef.current = true;

      const isMiniWindow = Boolean(window.opener) || window.name?.startsWith('mini_');
      if (isMiniWindow) {
        window.close();
        return;
      }
      savedOnLogout.current?.(isOpener);
    };

    // 1. Direct postMessage from child mini window to its opener
    const onWindowMessage = (e: MessageEvent) => {
      if (e.origin === window.location.origin && e.data?.type === AUTH_BROADCAST_MESSAGE.LOGOUT) {
        handleLogout(true);
      }
    };

    // 2. BroadcastChannel from other tabs
    const channel = getAuthBroadcastChannel();
    const onBroadcastMessage = (e: MessageEvent) => {
      if (e.data?.type === AUTH_BROADCAST_MESSAGE.LOGOUT) {
        handleLogout(false);
      }
    };

    // 3. Fallback check when tab returns from sleep/focus
    const onFocusOrVisible = () => {
      if (isLoggingOutRef.current || !auth.isAuthenticated || auth.isLoading) return;
      const user = getOidcUser();
      if (!user || user.expired) handleLogout(false);
    };

    window.addEventListener('message', onWindowMessage);
    channel?.addEventListener('message', onBroadcastMessage);
    const onVisibilityChange = () => document.visibilityState === 'visible' && onFocusOrVisible();
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onFocusOrVisible);

    return () => {
      window.removeEventListener('message', onWindowMessage);
      channel?.removeEventListener('message', onBroadcastMessage);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onFocusOrVisible);
    };
  }, [auth.isAuthenticated, auth.isLoading]);
};

export default useCrossTabAuthSync;
